import type { Event, EventHint } from '@sentry/react';
import { TRPCClientError } from '@trpc/client';

/** Placeholder for a procedure path or tRPC code the error does not carry. */
const UNKNOWN = 'unknown';

function isRecord(value: unknown): value is Record<PropertyKey, unknown> {
    return typeof value === 'object' && value !== null;
}

function readNonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Give every `TRPCClientError` the server answered an issue per procedure and tRPC code.
 *
 * The error is built inside the tRPC link, so its only stack frames are library frames and
 * Sentry's default hash is the same for every procedure: all failures used to share one issue
 * (CURIUM-FE-SV, then CURIUM-FE-TV), so a new 4xx regression never opened an issue of its own.
 * Running in `beforeSend` covers the cache handlers, the login callback and any later
 * `captureException(trpcError)`.
 *
 * The path comes from the server's error shape, else from the `trpcPath` tag the cache handler
 * derives from the query or mutation key (errors built client-side carry no path). Path and code
 * are procedure identifiers, not user payload. An event that already sets a fingerprint keeps it.
 *
 * Only an error the server answered (one carrying `data.code`) is fingerprinted. A code-less error
 * was built client-side (an edge gateway page, a parse or network failure); its linked cause, such
 * as an `EdgeResponseError` with in-app frames, already keeps those faults apart under the default
 * hash, and replacing it would open an issue per procedure for one gateway outage while merging an
 * edge 504 with a parse failure. Such an error keeps default grouping and is only tagged.
 */
export function applyTrpcClientErrorGrouping<TEvent extends Event>(event: TEvent, hint: EventHint | undefined): TEvent {
    const error = hint?.originalException;
    if (!(error instanceof TRPCClientError)) return event;

    const data: unknown = error.data;
    const path =
        (isRecord(data) ? readNonEmptyString(data.path) : undefined) ?? readNonEmptyString(event.tags?.trpcPath);
    const code = isRecord(data) ? readNonEmptyString(data.code) : undefined;
    const tags = { ...event.tags, trpcPath: path ?? UNKNOWN, trpcCode: code ?? UNKNOWN };
    if (code === undefined) return { ...event, tags };

    return {
        ...event,
        fingerprint: event.fingerprint ?? ['trpc-client-error', path ?? '{{ default }}', code],
        tags,
    };
}
